/* The Supabase client, and the queries behind `Source`. The app never queries tables
   directly; every read and write it makes is a function in this package, which keeps the
   surface row-level security has to cover in one place. */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { DataError, type Source } from "./collections";
import type { PracticeSource } from "./practice";

export type { SupabaseClient };

export interface SupabaseConfig {
  url: string;
  /** The publishable key (sb_publishable_…, or a legacy anon key): public by design, it only
      grants what row-level security allows. */
  publishableKey: string;
}

export const createSupabase = ({ url, publishableKey }: SupabaseConfig): SupabaseClient =>
  createClient(url, publishableKey);

const COLLECTION_COLUMNS =
  "id, owner_id, is_base, name, description, visibility, forked_from, spot_count, created_at, updated_at, published_at, moderated_at, moderation_note, opponents";
const PUBLIC_COLLECTION_COLUMNS =
  "id, name, description, spot_count, stakes, formats, published_at, updated_at, owner_name, copies, mine, is_base";
const SPOT_COLUMNS = "id, collection_id, key, meta, actions, cells, copied_from, updated_at";
// PostgREST caps a response at 1,000 rows; a collection may hold 2,000 spots.
const PAGE = 500;

export function supabaseSource(db: SupabaseClient): Source {
  return {
    async collections() {
      const { data, error } = await db.from("collections").select(COLLECTION_COLUMNS);
      if (error) throw new Error(`Could not list collections: ${error.message}`);
      return data;
    },

    async spots(collectionId) {
      const out: unknown[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await db
          .from("spots")
          .select(SPOT_COLUMNS)
          .eq("collection_id", collectionId)
          .order("key")
          .range(from, from + PAGE - 1);
        if (error) throw new Error(`Could not load the collection's spots: ${error.message}`);
        out.push(...data);
        if (data.length < PAGE) return out;
      }
    },

    async createCollection(row) {
      const { data, error } = await db
        .from("collections")
        .insert(row)
        .select(COLLECTION_COLUMNS)
        .single();
      if (error) throw new DataError(error.message, error.code);
      return data;
    },

    async updateCollection(id, patch) {
      const { data, error } = await db
        .from("collections")
        .update(patch)
        .eq("id", id)
        .select(COLLECTION_COLUMNS)
        .single();
      if (error) throw new DataError(error.message, error.code);
      return data;
    },

    async deleteCollection(id) {
      // Row-level security turns "not yours" into "no row": ask for the row back to know.
      const { data, error } = await db.from("collections").delete().eq("id", id).select("id");
      if (error) throw new DataError(error.message, error.code);
      if (!data.length) throw new DataError("That collection is not yours to delete.", "42501");
    },

    async setVisibility(id, visibility) {
      const { data, error } = await db
        .from("collections")
        .update({ visibility })
        .eq("id", id)
        .select(COLLECTION_COLUMNS)
        .single();
      if (error) throw new DataError(error.message, error.code);
      return data;
    },

    async publicCollections(only) {
      let query = db.from("public_collections").select(PUBLIC_COLLECTION_COLUMNS);
      if (only === "mine") query = query.eq("mine", true);
      const { data, error } = await query.order("published_at", { ascending: false });
      if (error) throw new Error(`Could not list the public collections: ${error.message}`);
      return data;
    },

    async copyCollection(sourceId, name) {
      const { data, error } = await db.rpc("copy_collection", { source: sourceId, new_name: name });
      if (error) throw new DataError(error.message, error.code);
      return String(data);
    },

    async insertSpot(collectionId, spot) {
      const { data, error } = await db
        .from("spots")
        .insert({ collection_id: collectionId, ...spot })
        .select(SPOT_COLUMNS)
        .single();
      if (error) throw new DataError(error.message, error.code);
      return data;
    },

    async updateSpot(collectionId, key, chart) {
      // No row back is an answer, not an error: the collection holds no such spot (or is not
      // the caller's, which row-level security makes look the same).
      const { data, error } = await db
        .from("spots")
        .update(chart)
        .eq("collection_id", collectionId)
        .eq("key", key)
        .select(SPOT_COLUMNS);
      if (error) throw new DataError(error.message, error.code);
      return data[0] ?? null;
    },

    async deleteSpot(collectionId, key) {
      const { data, error } = await db
        .from("spots")
        .delete()
        .eq("collection_id", collectionId)
        .eq("key", key)
        .select("id");
      if (error) throw new DataError(error.message, error.code);
      if (!data.length) throw new DataError("That chart is not yours to delete.", "42501");
    },
  };
}

/* The practice log's two inserts. "Ignore duplicates" is `on conflict do nothing`: the outbox
   may send a row twice, and the second time is not an error. No row is asked back. */
export function supabasePracticeSource(db: SupabaseClient): PracticeSource {
  return {
    async userId() {
      // Read from the client's own storage: no request, and so an answer offline too.
      const { data } = await db.auth.getSession();
      return data.session?.user.id ?? null;
    },

    async insertSessions(rows) {
      const { error } = await db
        .from("practice_sessions")
        .upsert(rows, { onConflict: "id", ignoreDuplicates: true });
      if (error) throw new DataError(error.message, error.code);
    },

    async insertAnswers(rows) {
      const { error } = await db
        .from("practice_answers")
        .upsert(rows, { onConflict: "session_id,seq", ignoreDuplicates: true });
      if (error) throw new DataError(error.message, error.code);
    },
  };
}
